import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { SearchEntity } from '@plato/search/search-types';
import { Badge } from '@plato/ui-legacy/components/ui/badge';
import { Button as LegacyButton } from '@plato/ui-legacy/components/ui/button';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useLocalStorage } from '@plato/ui-legacy/lib/hooks/use-local-storage';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { Button } from '@plato/ui/primitives/button';
import { Typography } from '@plato/ui/primitives/typography';
import { ArrowUpRightIcon, ChevronDownIcon, PlusIcon, ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TenderWinEventPayload } from '../../analytics/types/analytics-events.ts';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';

export type TenderHit = RouterOutput['platoUser']['tenderWins']['forCustomer']['hits'][number];
type TenderResponse = 'interested' | 'not_relevant';

const hiddenStorageKey = 'tender-win-hidden-notices';
// Same first page as InsightSection; each "show more" adds another page.
const pageSize = 3;

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

function buyerLine(hit: TenderHit): string {
    return hit.buyerCity ? `${hit.buyer}, ${hit.buyerCity}` : hit.buyer;
}

type TenderWinSectionProps = {
    hits: TenderHit[];
    customerId: string;
    customerName: string;
};

/**
 * Prototype (RETAIN-1390): public-tender awards this customer won or bid on, matched live against
 * oeffentlichevergabe.de. Laid out like `InsightCard` / `InsightDialog` so it reads as one more
 * insight; the rating stays in localStorage + PostHog instead of `ins__insight_outcome`.
 *
 * ponytail: becomes a real insight type once relevance is proven.
 */
export function TenderWinSection({
    hits: allHits,
    customerId,
    customerName,
}: TenderWinSectionProps): ReactElement | null {
    const [hiddenNotices, setHiddenNotices] = useLocalStorage<string[]>(hiddenStorageKey, []);
    const sendEvent = useAnalytics();
    const { t } = useTranslation('insights');
    const showCreateTaskDialog = useShowCreateTaskDialog();

    const hits = allHits.filter((hit) => !hiddenNotices.includes(hit.noticeUrl));
    const [visibleCount, setVisibleCount] = useState(pageSize);
    const hiddenCount = Math.max(hits.length - visibleCount, 0);

    const payload = (hit: TenderHit): TenderWinEventPayload => ({
        customer_id: customerId,
        notice_url: hit.noticeUrl,
        role: hit.role,
        confidence: hit.confidence,
        value_eur: hit.valueEur,
        buyer: hit.buyer,
    });

    // One viewed event per card per page visit; the ref dedups across re-renders.
    const viewed = useRef(new Set<string>());
    useEffect(() => {
        for (const hit of hits) {
            if (viewed.current.has(hit.noticeUrl)) {
                continue;
            }
            viewed.current.add(hit.noticeUrl);
            sendEvent('tender_win_viewed', payload(hit));
        }
    });

    if (hits.length === 0) {
        return null;
    }

    const rate = (hit: TenderHit, response: TenderResponse): void => {
        sendEvent('tender_win_rated', { ...payload(hit), response });
        setHiddenNotices((prev) => [...prev, hit.noticeUrl]);
    };

    const writeQuote = (hit: TenderHit): void => {
        showCreateTaskDialog({
            description: t('tender_win__task_description', { title: hit.title, url: hit.noticeUrl }),
            links: [{ modelId: customerId, modelType: SearchEntity.Customer, title: customerName }],
            source: 'tender_win',
        });
    };

    return (
        <section className="flex flex-col gap-default rounded-lg bg-white p-default">
            <div className="flex items-center gap-cozy">
                <Text size="sm" weight="semibold">
                    {t('tender_win__section_title')}
                </Text>
                {/* Same hardcoded, untranslated `Alpha` marker as Ask Plato. */}
                <Badge variant="blue" className="text-xs">
                    Alpha
                </Badge>
            </div>
            <div className="flex flex-col gap-default">
                {hits.slice(0, visibleCount).map((hit) => (
                    <TenderWinCard
                        key={hit.noticeUrl}
                        hit={hit}
                        onRate={(response) => {
                            rate(hit, response);
                        }}
                        onWriteQuote={() => {
                            writeQuote(hit);
                        }}
                        onNoticeOpened={() => {
                            sendEvent('tender_win_notice_opened', payload(hit));
                        }}
                    />
                ))}
                {hiddenCount > 0 && (
                    <div className="flex justify-end">
                        <LegacyButton
                            variant="link"
                            size="sm"
                            onClick={() => {
                                setVisibleCount((count) => count + pageSize);
                            }}
                        >
                            {t('show_more', { ns: 'common' })} ({hiddenCount})
                            <ChevronDownIcon className="ml-1 h-4 w-4" />
                        </LegacyButton>
                    </div>
                )}
            </div>
        </section>
    );
}

type TenderWinCardProps = {
    hit: TenderHit;
    onRate: (response: TenderResponse) => void;
    onWriteQuote: () => void;
    onNoticeOpened: () => void;
};

/** One card and its detail dialog, shaped like `InsightCard` + `InsightDialog`. */
function TenderWinCard({ hit, onRate, onWriteQuote, onNoticeOpened }: TenderWinCardProps): ReactElement {
    const { t } = useTranslation('insights');
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const close = (): void => {
        setIsDetailOpen(false);
    };

    const title = hit.role === 'winner' ? t('tender_win__title') : t('tender_win__title_bid');
    const published = dateFormat.format(new Date(hit.pubDay));
    const amount = hit.valueEur ? (
        <span className="font-bold tabular-nums text-green-600">
            <CurrencyOrEmpty>{hit.valueEur}</CurrencyOrEmpty>
        </span>
    ) : null;

    const facts: { label: string; value: ReactNode }[] = [
        { label: t('tender_win__fact_service'), value: hit.title },
        { label: t('tender_win__fact_buyer'), value: buyerLine(hit) },
        { label: t('tender_win__fact_published'), value: published },
        { label: t('tender_win__fact_value'), value: amount ?? t('tender_win__value_unknown') },
        {
            label: t('tender_win__fact_winner'),
            value: [hit.winnerName, hit.winnerStreet, hit.winnerPlz, hit.winnerCity]
                .filter(Boolean)
                .join(', '),
        },
        {
            label: t('tender_win__fact_match'),
            value: hit.confidence === 'high' ? t('tender_win__match_high') : t('tender_win__match_uncertain'),
        },
        { label: t('tender_win__fact_cpv'), value: hit.cpv.join(', ') || '–' },
    ];
    if (hit.lots.length > 0) {
        facts.splice(1, 0, { label: t('tender_win__fact_lots'), value: hit.lots.join(', ') });
    }

    return (
        <>
            <Dialog open={isDetailOpen} onClose={close}>
                <DialogContent>
                    <DialogHeader className="items-start">
                        <div className="flex min-w-0 flex-1 flex-col gap-tight">
                            <DialogTitle className="whitespace-normal">{title}</DialogTitle>
                            <Typography variant="body" className="truncate text-content-weak">
                                {buyerLine(hit)}
                            </Typography>
                            <div className="flex items-center gap-cozy pt-tight sm:hidden">{amount}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-cozy">
                            <div className="hidden items-center gap-cozy sm:flex">{amount}</div>
                            <DialogIconClose label={t('form__close', { ns: 'common' })} />
                        </div>
                    </DialogHeader>
                    <DialogBody>
                        <dl className="flex flex-col gap-cozy">
                            {facts.map((fact) => (
                                <div
                                    key={fact.label}
                                    className="flex flex-col gap-tight sm:flex-row sm:gap-default"
                                >
                                    <dt className="shrink-0 text-content-weak sm:w-40">
                                        <Typography variant="caption">{fact.label}</Typography>
                                    </dt>
                                    <dd className="min-w-0 break-words">
                                        <Typography variant="body">{fact.value}</Typography>
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        <a
                            href={hit.noticeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-default inline-flex items-center gap-tight text-content-weak transition-colors hover:text-content hover:underline"
                            onClick={onNoticeOpened}
                        >
                            <Typography variant="body">{t('tender_win__open_notice')}</Typography>
                            <ArrowUpRightIcon className="size-4 shrink-0" />
                        </a>
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            variant="secondary"
                            className="mr-auto"
                            onClick={() => {
                                close();
                                onRate('not_relevant');
                            }}
                        >
                            <ThumbsDownIcon /> {t('action__mark_as_not_relevant')}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                close();
                                onWriteQuote();
                            }}
                        >
                            <PlusIcon /> {t('tender_win__write_quote')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                close();
                                onRate('interested');
                            }}
                        >
                            <ThumbsUpIcon /> {t('tender_win__interested')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Card role="article">
                <CardFront>
                    <CardContent className="min-w-0">
                        <CardHeader className="flex-row flex-nowrap items-center gap-cozy">
                            <CardTitle className="min-w-0 grow-0 font-semibold">{title}</CardTitle>
                            {amount && (
                                <CardSubtitle className="order-none ml-auto shrink-0 items-center">
                                    {amount}
                                </CardSubtitle>
                            )}
                        </CardHeader>
                        <CardDetail className="text-content">{buyerLine(hit)}</CardDetail>
                        <CardDetail>
                            {hit.lots.length > 0 ? hit.lots.join(' · ') : hit.title}
                            {` · ${published}`}
                            {hit.confidence === 'medium' && ` · ${t('tender_win__match_uncertain')}`}
                        </CardDetail>
                    </CardContent>
                    <CardMainAction
                        aria-label={t('show_details', { ns: 'common' })}
                        onClick={() => {
                            setIsDetailOpen(true);
                        }}
                        className="absolute inset-0"
                    />
                </CardFront>
            </Card>
        </>
    );
}
